/**
 * ============================================
 * UCM - Ultra Car Manager
 * Public Header - Premium Sticky Version
 * Menu dinamico caricato da API
 * © 2024 AlbaSoft Studio - All Rights Reserved
 * ============================================
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useShowroom } from './PublicLayout';

interface MenuItem {
  id: string;
  label: string;
  path: string;
  order: number;
  isActive: boolean;
  openInNewTab: boolean;
  children: { id: string; label: string; path: string; order: number; isActive: boolean }[];
}

// Menu predefinito come fallback
const DEFAULT_MENU: MenuItem[] = [
  {
    id: 'noleggio',
    label: 'Noleggio',
    path: '/vetrina/noleggio',
    order: 1,
    isActive: true,
    openInNewTab: false,
    children: [
      { id: 'noleggio-all', label: 'Tutti i Veicoli', path: '/vetrina/noleggio', order: 1, isActive: true },
      { id: 'noleggio-suv', label: 'SUV & Crossover', path: '/vetrina/noleggio?bodyType=suv', order: 2, isActive: true },
      { id: 'noleggio-utilitarie', label: 'Utilitarie', path: '/vetrina/noleggio?bodyType=hatchback', order: 3, isActive: true },
      { id: 'noleggio-berline', label: 'Berline', path: '/vetrina/noleggio?bodyType=sedan', order: 4, isActive: true },
    ],
  },
  {
    id: 'vendita',
    label: 'Auto in Vendita',
    path: '/vetrina/vendita',
    order: 2,
    isActive: true,
    openInNewTab: false,
    children: [
      { id: 'vendita-all', label: 'Tutte le Auto', path: '/vetrina/vendita', order: 1, isActive: true },
      { id: 'vendita-suv', label: 'SUV & Crossover', path: '/vetrina/vendita?bodyType=suv', order: 2, isActive: true },
      { id: 'vendita-berline', label: 'Berline', path: '/vetrina/vendita?bodyType=sedan', order: 3, isActive: true },
      { id: 'vendita-km0', label: 'Km 0 / Semestrali', path: '/vetrina/vendita?maxKm=10000', order: 4, isActive: true },
    ],
  },
  {
    id: 'servizi',
    label: 'Servizi',
    path: '/vetrina/servizi',
    order: 3,
    isActive: true,
    openInNewTab: false,
    children: [
      { id: 'servizi-finanziamenti', label: 'Finanziamenti', path: '/vetrina/servizi/finanziamenti', order: 1, isActive: true },
      { id: 'servizi-permuta', label: 'Permuta', path: '/vetrina/servizi/permuta', order: 2, isActive: true },
      { id: 'servizi-garanzia', label: 'Garanzia', path: '/vetrina/servizi/garanzia', order: 3, isActive: true },
    ],
  },
  { id: 'chi-siamo', label: 'Chi Siamo', path: '/vetrina/chi-siamo', order: 4, isActive: true, openInNewTab: false, children: [] },
  { id: 'contatti', label: 'Contatti', path: '/vetrina/contatti', order: 5, isActive: true, openInNewTab: false, children: [] },
];

export default function PublicHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { favorites, compareList, settings } = useShowroom();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(DEFAULT_MENU);

  // Carica menu da API
  useEffect(() => {
    const loadMenu = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';
        const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'demo';

        const response = await fetch(`${apiUrl}/public/menu`, {
          headers: {
            'X-Tenant-Slug': tenantSlug,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.menu?.length > 0) {
            setMenuItems(data.data.menu);
          }
        }
      } catch (error) {
        console.error('Error loading menu:', error);
        // Usa menu predefinito in caso di errore
      }
    };

    loadMenu();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'shadow-lg' : ''
    }`}>
      {/* Top Bar */}
      <div className={`bg-slate-900 text-white transition-all duration-300 ${
        isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-10'
      }`}>
        <div className="container mx-auto px-4 h-full flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${settings?.contact?.phone || ''}`} className="flex items-center gap-2 hover:text-red-400 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="font-medium">{settings?.contact?.phone || '+39 02 1234567'}</span>
            </a>
            <a href={`mailto:${settings?.contact?.email || ''}`} className="hidden md:flex items-center gap-2 hover:text-red-400 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{settings?.contact?.email || 'info@demo.local'}</span>
            </a>
          </div>
          <div className="hidden md:flex items-center gap-2 text-slate-300">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{settings?.contact?.address || 'Via Roma 1'}, {settings?.contact?.city || 'Milano'}</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className={`bg-white border-b transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              {settings?.general?.logo ? (
                <img src={settings.general.logo} alt={settings.general.siteName} className={`transition-all duration-300 ${isScrolled ? 'h-10' : 'h-14'}`} />
              ) : (
                <div className={`flex items-center gap-2 transition-all duration-300 ${isScrolled ? 'scale-90' : ''}`}>
                  <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-bold text-xl text-slate-900">{settings?.general?.siteName || 'AutoSalone'}</span>
                    <span className="text-xs text-slate-500 block">Premium Cars</span>
                  </div>
                </div>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {menuItems.map((item) => (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={() => item.children?.length > 0 && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.path}
                    target={item.openInNewTab ? '_blank' : undefined}
                    rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-1 ${
                      isActive(item.path)
                        ? 'text-red-600 bg-red-50'
                        : 'text-slate-700 hover:text-red-600 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                    {item.children?.length > 0 && (
                      <svg className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </Link>

                  {/* Dropdown */}
                  {item.children?.length > 0 && activeDropdown === item.label && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 animate-fade-in">
                      {item.children.map((subItem) => (
                        <Link
                          key={subItem.id}
                          to={subItem.path}
                          className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                title="Cerca"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Favorites */}
              <Link
                to="/vetrina/preferiti"
                className="relative p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                title="Preferiti"
              >
                <svg className="w-5 h-5" fill={favorites.length > 0 ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {favorites.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {favorites.length}
                  </span>
                )}
              </Link>

              {/* Compare */}
              <Link
                to="/vetrina/confronta"
                className="relative p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                title="Confronta"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {compareList.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {compareList.length}
                  </span>
                )}
              </Link>

              {/* CTA Button */}
              <Link
                to="/vetrina/catalogo"
                className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-600/25"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Esplora</span>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t shadow-xl animate-slide-down">
          <nav className="container mx-auto px-4 py-4 space-y-1">
            {menuItems.map((item) => (
              <div key={item.id}>
                <Link
                  to={item.path}
                  target={item.openInNewTab ? '_blank' : undefined}
                  rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                  className={`block px-4 py-3 rounded-lg font-medium ${
                    isActive(item.path) ? 'text-red-600 bg-red-50' : 'text-slate-700'
                  }`}
                >
                  {item.label}
                </Link>
                {item.children?.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.children.map((subItem) => (
                      <Link
                        key={subItem.id}
                        to={subItem.path}
                        className="block px-4 py-2 text-sm text-slate-500 hover:text-red-600"
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/vetrina/catalogo"
              className="flex items-center justify-center gap-2 px-4 py-3 mt-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>Esplora Catalogo</span>
            </Link>
          </nav>
        </div>
      )}

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden animate-scale-in">
            <div className="p-4 border-b flex items-center gap-4">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Cerca marca, modello, tipo..."
                className="flex-1 text-lg outline-none"
                autoFocus
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-100"
                title="Chiudi ricerca"
                aria-label="Chiudi pannello di ricerca"
              >
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-500 mb-3">Ricerche popolari</p>
              <div className="flex flex-wrap gap-2">
                {['SUV', 'Diesel', 'Automatico', 'Fiat', 'BMW', 'Audi'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => {
                      setSearchOpen(false);
                      navigate(`/vetrina/catalogo?q=${tag}`);
                    }}
                    className="px-4 py-2 bg-slate-100 rounded-full text-sm hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
